import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useLiveBoard from "../hooks/useLiveBoard";
import { deltaOf, sparkline, isListedTicker } from "../data/mockData";

function SessionChip({ s }) {
  if (s === "HALT") return <span className="chip chip-halt" data-testid="session-halt">Halt</span>;
  if (s === "AH") return <span className="chip chip-ah" data-testid="session-ah">After hours</span>;
  return <span className="chip" data-testid="session-rth">RTH</span>;
}

function DeltaText({ d }) {
  if (d == null) return <span style={{ color: "var(--ink-muted)" }}>Δ —</span>;
  const cls = d > 0 ? "up" : d < 0 ? "down" : "";
  const sign = d > 0 ? "+" : d < 0 ? "−" : "±";
  return <span className={cls}>Δ {sign}{Math.abs(d).toFixed(2)}%</span>;
}

function Sparkline({ points = [], up = true, width = 96, height = 22 }) {
  if (!points.length) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = width / (points.length - 1);
  const path = points
    .map((y, i) => {
      const px = (i * stepX).toFixed(2);
      const py = (height - ((y - min) / range) * height).toFixed(2);
      return `${i === 0 ? "M" : "L"}${px},${py}`;
    })
    .join(" ");
  const color = up === null ? "var(--ink-muted)" : up ? "var(--up)" : "var(--down)";
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={path} fill="none" stroke={color} strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
}

function Card({ row }) {
  const d = deltaOf(row);
  const points = useMemo(
    () => sparkline(row.ticker.charCodeAt(0) + row.ticker.charCodeAt(1), d),
    [row, d]
  );
  const halted = row.session === "HALT";
  const fmt = (v, dp = 2) => (v == null ? "—" : Number(v).toFixed(dp));
  return (
    <div
      style={{
        borderTop: "1px solid var(--line)",
        borderLeft: "1px solid var(--line)",
        padding: "22px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        transition: "background-color 150ms ease",
        background: "transparent",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--paper-raised)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      data-testid={`listed-card-${row.ticker}`}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Link
          to={`/listed/${row.ticker.toLowerCase()}`}
          className="wordmark"
          style={{ fontSize: 26, letterSpacing: "-0.04em", color: "var(--ink)", textDecoration: "none" }}
          data-testid={`listed-ticker-${row.ticker}`}
        >
          {row.ticker}
        </Link>
        <SessionChip s={row.session} />
      </div>
      <div style={{ color: "var(--ink-muted)", fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase" }}>
        {row.name}
      </div>
      <div className="tabular" style={{ display: "flex", alignItems: "baseline", gap: 12, marginTop: 2 }}>
        <div style={{ fontSize: 22, letterSpacing: "-0.02em" }}>{fmt(row.pool)}</div>
        <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>NAV {fmt(row.nav)}</div>
      </div>
      <div className="tabular" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12 }}><DeltaText d={d} /></div>
        <Sparkline points={points} up={d == null ? null : d > 0} />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <Link
          to={`/listed/${row.ticker.toLowerCase()}?side=buy`}
          className={`btn-primary ${halted ? "disabled" : ""}`}
          style={{ padding: "9px 14px", fontSize: 11.5, textDecoration: "none", flex: 1, letterSpacing: "0.06em" }}
          data-testid={`buy-${row.ticker}`}
        >
          {halted ? "HALT" : "BUY"}
        </Link>
        <Link
          to={`/listed/${row.ticker.toLowerCase()}?side=sell`}
          className={`btn-outline ${halted ? "disabled" : ""}`}
          style={{ padding: "9px 14px", fontSize: 11.5, textDecoration: "none", flex: 1, letterSpacing: "0.06em" }}
          data-testid={`sell-${row.ticker}`}
        >
          SELL
        </Link>
      </div>
      <div className="tabular" style={{ fontSize: 11, color: "var(--ink-muted)", letterSpacing: "0.06em" }}>
        vol {row.vol}
      </div>
    </div>
  );
}

export default function Listed() {
  const rows = useLiveBoard(1600);
  const [q, setQ] = useState("");
  const [view, setView] = useState("grid");

  const filtered = useMemo(() => {
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.ticker.toLowerCase().includes(q.toLowerCase()) ||
        r.name.toLowerCase().includes(q.toLowerCase())
    );
  }, [q, rows]);

  const copycatWarn = q && !isListedTicker(q) && q.trim().length >= 3;

  return (
    <div style={{ maxWidth: 1520, margin: "0 auto", padding: "48px 28px 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
        <h1 className="serif" style={{ fontSize: 52, letterSpacing: "-0.02em", margin: 0 }} data-testid="listed-title">
          Listed.
        </h1>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button onClick={() => setView("grid")} className="btn-ghost" style={{ opacity: view === "grid" ? 1 : 0.4 }} data-testid="view-grid">Grid</button>
          <span style={{ color: "var(--ink-muted)" }}>/</span>
          <button onClick={() => setView("table")} className="btn-ghost" style={{ opacity: view === "table" ? 1 : 0.4 }} data-testid="view-table">Table</button>
        </div>
      </div>

      <div style={{ borderBottom: "1px solid var(--line)", marginBottom: 20 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="NVDA, SPY…"
          data-testid="listed-search"
          style={{
            width: "100%", padding: "14px 2px", background: "transparent", border: "none", outline: "none",
            fontSize: 17, color: "var(--ink)", letterSpacing: "-0.005em",
          }}
        />
      </div>

      {copycatWarn && (
        <div
          data-testid="copycat-warn"
          style={{
            borderTop: "1px solid var(--down)", borderBottom: "1px solid var(--down)",
            padding: "12px 0", color: "var(--down)", fontSize: 13, marginBottom: 20,
          }}
        >
          If it is not on the list, it is not that name.
        </div>
      )}

      {view === "grid" ? (
        <div
          style={{
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
            borderRight: "1px solid var(--line)", borderBottom: "1px solid var(--line)",
          }}
          data-testid="listed-grid"
        >
          {filtered.map((r) => (<Card key={r.ticker} row={r} />))}
        </div>
      ) : (
        <table className="book tabular" data-testid="listed-table">
          <thead>
            <tr><th>Ticker</th><th>Name</th><th>Pool</th><th>NAV</th><th>Δ</th><th>Session</th><th>Vol</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const d = deltaOf(r);
              return (
                <tr key={r.ticker}>
                  <td><Link to={`/listed/${r.ticker.toLowerCase()}`} style={{ color: "var(--ink)" }}>{r.ticker}</Link></td>
                  <td style={{ color: "var(--ink-muted)" }}>{r.name}</td>
                  <td>{r.pool == null ? "—" : r.pool.toFixed(2)}</td>
                  <td style={{ color: "var(--ink-muted)" }}>{r.nav == null ? "—" : r.nav.toFixed(2)}</td>
                  <td><DeltaText d={d} /></td>
                  <td><SessionChip s={r.session} /></td>
                  <td>{r.vol}</td>
                  <td><Link to={`/listed/${r.ticker.toLowerCase()}`} className="btn-ghost">Open</Link></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
